import {
    cloneElement,
    CSSProperties,
    FC,
    isValidElement,
    KeyboardEvent,
    ReactElement,
    ReactNode,
    useState,
} from 'react';
import type { CardProps } from 'antd';
import { Avatar, Card, Col, Row } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

export interface PlatformActionCardProps extends Omit<
    CardProps,
    'children' | 'onBlur' | 'onClick' | 'onFocus' | 'onKeyDown' | 'onMouseEnter' | 'onMouseLeave' | 'styles'
> {
    description?: ReactNode;
    disabled?: boolean;
    icon?: ReactNode;
    label: ReactNode;
    onPress: () => void;
    trailing?: ReactNode;
}

const PlatformActionCard: FC<PlatformActionCardProps> = ({
    description,
    disabled = false,
    icon,
    label,
    onPress,
    style,
    trailing,
    ...cardProps
}) => {
    const { token } = useAntDesignTheme();
    const [focused, setFocused] = useState(false);
    const [hovered, setHovered] = useState(false);
    const iconAvatarStyle: CSSProperties = {
        background: token.colorBgBase,
        border: `1px solid ${token.colorBorder}`,
        color: token.colorTextTertiary,
        verticalAlign: '-0.225rem',
    };

    const renderIcon = () => {
        if (!icon) {
            return null;
        }

        if (isValidElement(icon) && icon.type === Avatar) {
            const avatarIcon = icon as ReactElement<{
                icon?: ReactNode;
                shape?: 'circle' | 'square';
                size?: number;
                style?: CSSProperties;
            }>;

            return cloneElement(avatarIcon, {
                shape: 'square',
                size: 16,
                style: {
                    ...avatarIcon.props.style,
                    ...iconAvatarStyle,
                },
            });
        }

        return (
            <Avatar
                icon={
                    <span
                        style={{
                            alignItems: 'center',
                            display: 'inline-flex',
                            justifyContent: 'center',
                            lineHeight: 0,
                            transform: 'scale(0.75)',
                            transformOrigin: 'center',
                        }}
                    >
                        {icon}
                    </span>
                }
                shape='square'
                size={16}
                style={iconAvatarStyle}
            />
        );
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (disabled || (event.key !== 'Enter' && event.key !== ' ')) {
            return;
        }

        event.preventDefault();
        onPress();
    };

    return (
        <Card
            {...cardProps}
            aria-disabled={disabled}
            role='button'
            tabIndex={disabled ? -1 : 0}
            styles={{ body: { padding: '0.5rem 0.625rem' } }}
            style={{
                backgroundColor: (focused || hovered) && !disabled ? token.colorBgBase : undefined,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.55 : 1,
                outline: focused && !disabled ? `2px solid ${token.colorPrimaryBorder}` : undefined,
                outlineOffset: focused && !disabled ? 2 : undefined,
                textAlign: 'left',
                width: '100%',
                ...style,
            }}
            onBlur={() => setFocused(false)}
            onClick={disabled ? undefined : onPress}
            onFocus={() => setFocused(true)}
            onKeyDown={handleKeyDown}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Row align='middle' wrap={false}>
                {icon ? <Col flex='none'>{renderIcon()}</Col> : null}
                <Col flex='none'>
                    <span style={{ marginInlineStart: icon ? '0.375rem' : undefined }}>{label}</span>
                </Col>
                <Col flex='auto' />
                {trailing ? <Col flex='none'>{trailing}</Col> : null}
            </Row>
            {description ? (
                <Row
                    align='middle'
                    style={{ color: token.colorTextTertiary, fontSize: token.fontSizeSM, marginTop: '0.375rem' }}
                >
                    {description}
                </Row>
            ) : null}
        </Card>
    );
};

export default PlatformActionCard;
